import { Icon, Tooltip, TooltipContent, TooltipTrigger } from "@nexus/ui"
import { useTranslation } from "react-i18next"
import { getDualSpendDisplay } from "../../../lib/dual-spend-display"

export interface OpportunityMetricsProps {
  yourSpend: number | null
  yourSpendPerDataset: number | null
  competitorSpend: number | null
  competitorSpendPerDataset: number | null
  benchmarkSpend: number | null
  benchmarkSpendPerDataset: number | null
  flowName: string
}

function MetricCard({
  label,
  perDataset,
  totalCreatives,
  icon,
  accentBg,
  accentText,
  barColor,
  barWidth,
  tooltip,
  perDatasetTooltip,
  totalCreativesTooltip,
}: {
  label: string
  perDataset: number | null
  totalCreatives: number | null
  icon: "user" | "users" | "building-2"
  accentBg: string
  accentText: string
  barColor: string
  barWidth: number
  tooltip?: string
  perDatasetTooltip: string
  totalCreativesTooltip: string
}) {
  const { t } = useTranslation()
  const display = getDualSpendDisplay(perDataset, totalCreatives, 1)

  return (
    <div className="relative flex flex-1 flex-col gap-2 overflow-hidden rounded-lg border bg-card pb-4 pt-3.5 px-3.5">
      <div className="flex items-center gap-2">
        <div className={`flex size-6 shrink-0 items-center justify-center rounded-md ${accentBg}`}>
          <Icon name={icon} size="xs" className={accentText} />
        </div>
        <span className="text-xs font-medium text-muted-foreground leading-tight">{label}</span>
      </div>
      <div className="flex items-baseline gap-2 flex-wrap">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-xl font-medium tabular-nums text-foreground leading-none cursor-help">
              {display.primary}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">{perDatasetTooltip}</TooltipContent>
        </Tooltip>
        {display.secondaryValue && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-xs tabular-nums text-muted-foreground cursor-help">
                {t("opportunities.spend.totalCreativesLabel", {
                  value: display.secondaryValue,
                  defaultValue: "{{value}} of total creatives",
                })}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">{totalCreativesTooltip}</TooltipContent>
          </Tooltip>
        )}
        {tooltip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex cursor-help">
                <Icon name="info" size="xs" className="text-muted-foreground/50" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">{tooltip}</TooltipContent>
          </Tooltip>
        )}
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-border/30">
        <div
          className={`h-full transition-all duration-700 ease-out ${barColor}`}
          style={{ width: `${barWidth}%` }}
        />
      </div>
    </div>
  )
}

export function OpportunityMetrics({
  yourSpend,
  yourSpendPerDataset,
  competitorSpend,
  competitorSpendPerDataset,
  benchmarkSpend,
  benchmarkSpendPerDataset,
  flowName,
}: OpportunityMetricsProps) {
  const { t } = useTranslation()
  const yourDisplay = getDualSpendDisplay(yourSpendPerDataset, yourSpend, 1)
  const competitorDisplay = getDualSpendDisplay(competitorSpendPerDataset, competitorSpend, 1)
  const benchmarkDisplay = getDualSpendDisplay(benchmarkSpendPerDataset, benchmarkSpend, 1)
  const maxSpend = Math.max(
    yourDisplay.barValue ?? 0,
    competitorDisplay.barValue ?? 0,
    benchmarkDisplay.barValue ?? 0,
  )
  const safeMax = maxSpend === 0 ? 1 : maxSpend
  const naTooltip = t("opportunities.drawer.spendUnavailableTooltip")
  const perDatasetTooltip = t("opportunities.spend.perAngleTooltip", {
    dataset: flowName,
    defaultValue: "Share of creatives within this {{dataset}} bucket (feature + sub-feature).",
  })
  const totalCreativesTooltip = t("opportunities.spend.totalCreativesTooltip", {
    defaultValue: "Share of the entity's total creatives in the reporting period.",
  })

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <MetricCard
        label={t("opportunities.drawer.yourSpend")}
        perDataset={yourSpendPerDataset}
        totalCreatives={yourSpend}
        icon="user"
        accentBg="bg-status-info-subtle"
        accentText="text-status-info"
        barColor="bg-status-info"
        barWidth={yourDisplay.barValue !== null ? (yourDisplay.barValue / safeMax) * 100 : 0}
        tooltip={yourDisplay.unavailable ? naTooltip : undefined}
        perDatasetTooltip={perDatasetTooltip}
        totalCreativesTooltip={totalCreativesTooltip}
      />
      <MetricCard
        label={t("opportunities.drawer.competitorSpend")}
        perDataset={competitorSpendPerDataset}
        totalCreatives={competitorSpend}
        icon="users"
        accentBg="bg-status-warning-subtle"
        accentText="text-status-warning"
        barColor="bg-status-warning"
        barWidth={competitorDisplay.barValue !== null ? (competitorDisplay.barValue / safeMax) * 100 : 0}
        tooltip={competitorDisplay.unavailable ? naTooltip : undefined}
        perDatasetTooltip={perDatasetTooltip}
        totalCreativesTooltip={totalCreativesTooltip}
      />
      <MetricCard
        label={t("opportunities.drawer.benchmarkSpend")}
        perDataset={benchmarkSpendPerDataset}
        totalCreatives={benchmarkSpend}
        icon="building-2"
        accentBg="bg-status-success-subtle"
        accentText="text-status-success"
        barColor="bg-status-success"
        barWidth={benchmarkDisplay.barValue !== null ? (benchmarkDisplay.barValue / safeMax) * 100 : 0}
        tooltip={benchmarkDisplay.unavailable ? naTooltip : undefined}
        perDatasetTooltip={perDatasetTooltip}
        totalCreativesTooltip={totalCreativesTooltip}
      />
    </div>
  )
}
